import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { AppContext } from '../../context/AppContext';
import { AuthContext } from '../../context/AuthContext';
import SafeIcon from '../../common/SafeIcon';
import InviteOpen from './InviteOpen';
import InviteResponse from './InviteResponse';
import InviteDateType from './InviteDateType';
import InviteDateTime from './InviteDateTime';
import InvitePreview from './InvitePreview';
import InviteSenderProfile from './InviteSenderProfile';
import InviteSend from './InviteSend';

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric'
  });
};

const InviteView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const {
    getInvitationByToken, updateInvitationStatus,
    updateInvitationWithChoices, createConnectionFromInvite,
    getSenderProfile, submitReply
  } = useContext(AppContext);
  const { isAuthenticated, user } = useContext(AuthContext);

  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState('loading');
  const [choices, setChoices] = useState({ dateType: '', customType: '', eventDate: '', eventTime: '', message: '' });
  const [senderProfile, setSenderProfile] = useState(null);
  const [sending, setSending] = useState(false);
  const [replyError, setReplyError] = useState('');
  const [declineMessage, setDeclineMessage] = useState('');

  useEffect(() => {
    const loadInvite = async () => {
      const data = await getInvitationByToken(id);
      if (!data) { setLoading(false); setStep('not-found'); return; }
      setInvite(data);
      localStorage.setItem('pendingInviteToken', id);

      if (data.status === 'cancelled') {
        setStep('cancelled');
        setLoading(false);
        return;
      }

      if (data.sender_id === user?.id) {
        setStep('sender-view');
      } else if (data.status === 'pending' || data.status === 'draft') {
        setStep('open');
      } else if (data.status === 'opened') {
        setStep('respond');
      } else if (data.status === 'declined') {
        setStep('declined');
      } else if (data.status === 'replied') {
        setStep('replied-done');
      } else if (data.status === 'accepted') {
        setStep('date-type');
      } else if (data.status === 'planning') {
        setStep('date-time');
      } else if (data.status === 'proposed') {
        setStep('waiting');
      } else if (data.status === 'confirmed') {
        setStep('confirmed');
      } else {
        setStep('respond');
      }

      setLoading(false);
    };
    loadInvite();
  }, [id, getInvitationByToken, user?.id]);

  useEffect(() => {
    if (isAuthenticated && user && invite && invite.sender_id !== user.id) {
      createConnectionFromInvite(id, user.id);
    }
  }, [isAuthenticated, user, invite, id, createConnectionFromInvite]);

  const handleOpen = () => {
    setStep('respond');
    updateInvitationStatus(id, 'opened');
  };

  const handleAccept = () => {
    updateInvitationStatus(id, 'accepted');
    setStep('date-type');
  };

  const handleDecline = () => {
    updateInvitationStatus(id, 'declined');
    setStep('declined');
  };

  const handleReply = async (response, message) => {
    setSending(true);
    setReplyError('');

    try {
      // Ensure connection is established before replying
      if (isAuthenticated && user && invite && invite.sender_id !== user.id) {
        await createConnectionFromInvite(id, user.id);
      }

      if (submitReply) {
        const result = await submitReply(id, response, message);
        setSending(false);
        if (result?.success) {
          setStep('replied-done');
        } else {
          setReplyError(result?.error || 'Failed to send reply. Please try again.');
        }
      } else {
        const result = await updateInvitationWithChoices(id, { ...choices, message, receiver_message: message });
        setSending(false);
        if (result?.success) {
          setStep('replied-done');
        } else {
          setReplyError(result?.error || 'Failed to send reply. Please try again.');
        }
      }
    } catch (err) {
      setSending(false);
      setReplyError('Something went wrong. Please try again.');
      console.error('handleReply error:', err);
    }
  };

  const handleMaybe = async (message) => {
    await handleReply('maybe', message || 'Maybe another time!');
  };

  const handleContinueAfterDecline = useCallback(async () => {
    if (!senderProfile) {
      const profile = await getSenderProfile(id);
      setSenderProfile(profile);
    }
    setStep('declined-profile');
  }, [id, getSenderProfile, senderProfile]);

  const handleDeclinedProfileBack = () => setStep('declined-message');

  const handleSendDeclineMessage = async () => {
    setSending(true);
    const result = await updateInvitationWithChoices(id, { ...choices, message: declineMessage, receiver_message: declineMessage });
    setSending(false);
    if (result.success) setStep('declined-sent');
  };

  const handleDateTypeContinue = () => { updateInvitationStatus(id, 'planning'); setStep('date-time'); };
  const handleDateTimeContinue = () => setStep('preview');
  const handleViewProfile = async () => {
    if (!senderProfile) { const profile = await getSenderProfile(id); setSenderProfile(profile); }
    setStep('sender-profile');
  };
  const handleSendPlan = async () => {
    setSending(true);
    const result = await updateInvitationWithChoices(id, choices);
    setSending(false);
    if (result.success) setStep('sent');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-pink-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-4 border-pink-200 border-t-pink-500 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-500 text-sm">Loading invitation...</p>
        </div>
      </div>
    );
  }

  if (step === 'cancelled') {
    return (
      <div className="min-h-screen bg-pink-50 flex items-center justify-center p-6">
        <div className="text-center">
          <div className="text-5xl mb-4">🚫</div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">Invitation Cancelled</h2>
          <p className="text-gray-500 text-sm">This invitation has been cancelled by the sender.</p>
          <button onClick={() => navigate('/')} className="mt-6 text-pink-500 font-semibold">Go to DayClap</button>
        </div>
      </div>
    );
  }

  if (step === 'not-found') {
    return (
      <div className="min-h-screen bg-pink-50 flex items-center justify-center p-6">
        <div className="text-center">
          <div className="text-5xl mb-4">😕</div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">Invitation not found</h2>
          <p className="text-gray-500 text-sm">This invitation may have expired or doesn't exist.</p>
          <button onClick={() => navigate('/')} className="mt-6 text-pink-500 font-semibold">Go to DayClap</button>
        </div>
      </div>
    );
  }

  if (step === 'sender-view' && invite) {
    const isSingles = invite.flow_type === 'singles';
    return (
      <div className="min-h-screen bg-pink-50 flex flex-col items-center justify-center p-6">
        <div className="w-full max-w-sm text-center">
          <div className="text-5xl mb-4">💌</div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">Your {isSingles ? 'Invitation' : 'Love Card'}</h2>
          <p className="text-gray-500 mb-1">Sent to: <span className="font-semibold">{invite.recipient_name}</span></p>
          {invite.event_date && (
            <p className="text-gray-500 mb-1 flex items-center justify-center gap-1">
              <SafeIcon name="Calendar" className="w-3.5 h-3.5" />
              Proposed: <span className="font-semibold">{formatDate(invite.event_date)}</span>
            </p>
          )}
          <p className="text-gray-500 mb-4">Status: <span className="font-semibold capitalize">{invite.status}</span></p>
          {invite.status === 'replied' && (
            <div className="bg-white rounded-2xl border border-green-200 p-5 mb-6 text-left">
              <p className="text-xs text-green-500 font-semibold uppercase mb-2">Reply from {invite.recipient_name}</p>
              {invite.reply_response && (
                <div className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold mb-2 ${
                  invite.reply_response === 'yes' ? 'bg-green-100 text-green-700' :
                  invite.reply_response === 'maybe' ? 'bg-amber-100 text-amber-700' :
                  'bg-gray-100 text-gray-600'
                }`}>
                  {invite.reply_response === 'yes' && '❤️ YES'}
                  {invite.reply_response === 'maybe' && '🤔 MAYBE'}
                  {invite.reply_response === 'no' && '❌ NO'}
                </div>
              )}
              {invite.reply_message && <p className="text-gray-700 italic text-sm">"{invite.reply_message}"</p>}
            </div>
          )}
          <button onClick={() => navigate('/home')} className="mt-4 bg-gradient-to-r from-pink-500 to-rose-500 text-white font-bold py-3 px-6 rounded-xl shadow-lg">
            Go Home
          </button>
        </div>
      </div>
    );
  }

  if (step === 'replied-done') {
    return (
      <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} className="min-h-screen bg-gradient-to-b from-pink-50 to-white flex flex-col items-center justify-center p-6">
        <div className="w-full max-w-sm text-center">
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', stiffness: 200, damping: 15 }} className="w-20 h-20 bg-gradient-to-br from-green-100 to-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
            <span className="text-4xl">💌</span>
          </motion.div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Reply Sent! ✨</h2>
          <p className="text-gray-500 mb-8">Your reply has been sealed in an envelope and sent back to {invite?.sender_profile?.name || invite?.sender_display_name || 'them'}!</p>
          <button onClick={() => navigate('/home')} className="w-full bg-gradient-to-r from-pink-500 to-rose-500 text-white font-bold py-4 rounded-xl shadow-lg shadow-pink-200">Go to DayClap</button>
        </div>
      </motion.div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 relative overflow-hidden flex flex-col justify-center items-center">
      <AnimatePresence>
        {step === 'open' && <InviteOpen onOpen={handleOpen} invite={invite} />}
      </AnimatePresence>

      {step === 'respond' && invite && (
        <InviteResponse
          invite={invite}
          onAccept={handleAccept}
          onDecline={handleDecline}
          onMaybe={handleMaybe}
          onReply={handleReply}
          sending={sending}
          replyError={replyError}
        />
      )}

      {step === 'declined' && (
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="w-full max-w-sm p-6 text-center">
          <div className="text-5xl mb-4">😊</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">No worries!</h2>
          <p className="text-gray-500 mb-8">Maybe another time 😊</p>
          <button onClick={handleContinueAfterDecline} className="w-full bg-gradient-to-r from-pink-500 to-rose-500 text-white font-bold py-4 rounded-xl shadow-lg shadow-pink-200">Continue →</button>
        </motion.div>
      )}

      {step === 'declined-profile' && (
        <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="w-full max-w-sm p-6">
          <button onClick={handleDeclinedProfileBack} className="mb-4 text-pink-500 font-semibold flex items-center gap-1">
            <SafeIcon name="ChevronLeft" /> Continue
          </button>
          <div className="bg-white rounded-2xl border border-gray-200 p-6 text-center">
            <p className="text-sm text-purple-500 font-semibold mb-3">A little about {senderProfile?.name || 'them'}</p>
            <img src={senderProfile?.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(senderProfile?.name || 'User')}&background=random`} alt="" className="w-24 h-24 rounded-full mx-auto mb-4 border-4 border-pink-100" />
            <h2 className="text-2xl font-bold text-gray-800 mb-1">{senderProfile?.name}</h2>
            {senderProfile?.bio && <p className="text-gray-600 text-sm leading-relaxed mb-4 italic">"{senderProfile.bio}"</p>}
          </div>
          <button onClick={handleDeclinedProfileBack} className="w-full mt-4 bg-gradient-to-r from-pink-500 to-rose-500 text-white font-bold py-4 rounded-xl shadow-lg shadow-pink-200">Send a Friendly Note →</button>
        </motion.div>
      )}

      {step === 'declined-message' && (
        <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="w-full max-w-sm p-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-2 text-center">Send a friendly note 💌</h2>
          <div className="bg-white rounded-2xl border border-gray-200 p-5 mb-6">
            <textarea value={declineMessage} onChange={(e) => setDeclineMessage(e.target.value)} placeholder="Thanks so much for thinking of me!" className="w-full p-4 rounded-xl border border-gray-200 focus:border-pink-500 outline-none resize-none h-32 text-gray-700" />
          </div>
          <button onClick={handleSendDeclineMessage} disabled={sending || !declineMessage.trim()} className="w-full bg-gradient-to-r from-pink-500 to-rose-500 text-white font-bold py-4 rounded-xl shadow-lg shadow-pink-200 disabled:opacity-50 flex items-center justify-center gap-2">
            {sending ? 'Sending...' : 'Send Note'}
          </button>
        </motion.div>
      )}

      {step === 'declined-sent' && (
        <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} className="w-full max-w-sm p-6 text-center">
          <div className="text-6xl mb-4">💛</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">All done!</h2>
          <p className="text-gray-500 mb-8">Maybe next time will be the one! 😊</p>
          <button onClick={() => navigate('/home')} className="w-full bg-gradient-to-r from-pink-500 to-rose-500 text-white font-bold py-4 rounded-xl shadow-lg shadow-pink-200">Go to DayClap</button>
        </motion.div>
      )}

      {step === 'date-type' && (
        <InviteDateType selected={choices.dateType} customValue={choices.customType}
          onSelect={(t) => setChoices({ ...choices, dateType: t, customType: t !== 'custom' ? '' : choices.customType })}
          onCustomChange={(v) => setChoices({ ...choices, customType: v })} onContinue={handleDateTypeContinue} />
      )}
      {step === 'date-time' && (
        <InviteDateTime eventDate={choices.eventDate} eventTime={choices.eventTime} message={choices.message}
          onDateChange={(d) => setChoices({ ...choices, eventDate: d })} onTimeChange={(t) => setChoices({ ...choices, eventTime: t })}
          onMessageChange={(m) => setChoices({ ...choices, message: m })} onContinue={handleDateTimeContinue} />
      )}
      {step === 'preview' && invite && (
        <InvitePreview invite={invite} choices={choices} onEdit={() => setStep('date-type')} onContinue={() => setStep('send')} onViewProfile={handleViewProfile} />
      )}
      {step === 'sender-profile' && (
        <InviteSenderProfile profile={senderProfile || invite?.sender_profile} onBack={() => setStep('preview')} />
      )}
      {step === 'send' && invite && (
        <InviteSend invite={invite} choices={choices} onSend={handleSendPlan} sending={sending} />
      )}
      {step === 'sent' && (
        <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} className="w-full max-w-sm p-6 text-center">
          <div className="text-6xl mb-4">🎉</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Sent! ❤️</h2>
          <p className="text-gray-500 mb-8">Your date plan has been sent!</p>
          <button onClick={() => navigate('/home')} className="w-full bg-gradient-to-r from-pink-500 to-rose-500 text-white font-bold py-4 rounded-xl shadow-lg shadow-pink-200">Go to DayClap</button>
        </motion.div>
      )}
      {step === 'waiting' && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="w-full max-w-sm p-6 text-center">
          <div className="text-5xl mb-4">⏳</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Waiting for response</h2>
          <p className="text-gray-500 mb-8">Your plan has been sent!</p>
          <button onClick={() => navigate('/home')} className="bg-white border border-gray-200 text-gray-700 font-bold py-3 px-6 rounded-xl">Go to DayClap</button>
        </motion.div>
      )}
      {step === 'confirmed' && (
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="w-full max-w-sm p-6 text-center">
          <div className="text-6xl mb-4">🎉</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">It's a plan! ❤️</h2>
          <button onClick={() => navigate('/home')} className="w-full bg-gradient-to-r from-pink-500 to-rose-500 text-white font-bold py-4 rounded-xl shadow-lg">Go to DayClap</button>
        </motion.div>
      )}

      <div className="absolute top-0 left-0 w-full h-full pointer-events-none overflow-hidden -z-10">
        <div className="absolute top-10 left-10 w-32 h-32 bg-pink-200 rounded-full mix-blend-multiply filter blur-2xl opacity-50 animate-blob" />
        <div className="absolute top-20 right-10 w-32 h-32 bg-purple-200 rounded-full mix-blend-multiply filter blur-2xl opacity-50 animate-blob animation-delay-2000" />
        <div className="absolute -bottom-8 left-20 w-32 h-32 bg-rose-200 rounded-full mix-blend-multiply filter blur-2xl opacity-50 animate-blob animation-delay-4000" />
      </div>
    </div>
  );
};

export default InviteView;